import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpContextToken,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { Observable } from "rxjs";
import { catchError, finalize, timeout } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { AccountService } from "../../customer-portal/services/account.service";
import { WindowRef } from "../common/global-refs";
import { LoaderService } from "./loader.service";
import { NotificationService } from "./notification.service";

const ERROR_MESSAGES = {
  INTERNET_CONNECTION: "Please check your internet connection and try again",
  DEFAULT: "Something went wrong",
};
// matIconRegistry requests should be made without interceptor
const ICON_URL = "assets/images/custom-icons";

// Optional: allow callers to opt out of base prefixing per-request
export const SKIP_BASE_URL = new HttpContextToken<boolean>(() => false);

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  constructor(
    private readonly cookieService: CookieService,
    private readonly router: Router,
    private readonly notificationService: NotificationService,
    private readonly windowRef: WindowRef,
    private readonly loaderService: LoaderService,
    private readonly dialogRef: MatDialog,
    private readonly accountService: AccountService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (
      request.url !== environment.API_TOKEN_URL &&
      !request.url.includes(ICON_URL)
    ) {
      let headers = request.headers;
      const accessToken = this.cookieService.get("access_token");
      const requestType = request.params.get("requestType");
      const requestResponseType = request.responseType;
      const method = request.method.toUpperCase();

      // Only set Content-Type for requests with a body (POST, PUT, PATCH, etc.)
      // GET and DELETE requests should not have Content-Type header
      if (requestType !== "upload") {
        if (method !== "GET" && method !== "DELETE" && method !== "HEAD") {
          headers = headers.set("Content-Type", "application/json");
        } else {
          // Explicitly remove Content-Type for GET/DELETE/HEAD requests
          headers = headers.delete("Content-Type");
        }
      }

      // Only add Authorization header if we have a token
      // Azure API might need different handling, but for now we'll try with the same token
      if (accessToken) {
        headers = headers.set("Authorization", `Bearer ${accessToken}`);
      }

      let URL = environment.API_URL;
      if (requestType === "v4Functions") {
        URL = environment.V4_FUNCTIONS_API_URL;
      }

      // Only prefix relative URLs; leave absolute URLs intact
      const isAbsolute = /^https?:\/\//i.test(request.url);
      const skipBase = request.context.get(SKIP_BASE_URL) === true;
      const shouldPrefix = !isAbsolute && !skipBase;

      // Normalize slashes
      const base = (URL || "").replace(/\/$/, "");
      const path = request.url.replace(/^\/+/, "");
      const finalUrl = shouldPrefix ? `${base}/${path}` : request.url;

      request = request.clone({
        params: request.params.delete("requestType"),
        headers,
        responseType: requestResponseType || "json",
        url: finalUrl,
      });

      // send request without spinner and centralized error handling
      if (requestType === "silent") {
        return next.handle(request);
      }
    }

    this.loaderService.pushRequest();

    return next.handle(request).pipe(
      timeout(400000),
      catchError((res) => {
        this.handleError(res);
        throw res;
      }),
      finalize(() => {
        this.loaderService.removeRequest();
      }),
    );
  }

  private getError(res: HttpErrorResponse): string {
    if (!this.windowRef.nativeWindow.navigator.onLine) {
      return ERROR_MESSAGES.INTERNET_CONNECTION;
    }

    return (
      (res.error && (res.error.Message || res.error.error_description)) ||
      res.statusText ||
      ERROR_MESSAGES.DEFAULT
    );
  }

  private handleError(res: HttpErrorResponse): void {
    if (res.status === 401) {
      this.dialogRef.closeAll(); // close all opened dialogs on session expiration
      this.accountService.resetAccount();
      this.router.navigate(["/login"]);
    } else {
      const error = this.getError(res);
      this.notificationService.error(error);
    }
  }
}
